import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react'
import { JarvisOrb } from '@/components/JarvisOrb'
import { useToast } from '@/components/Toast'
import { apiClient, unwrap } from '@/lib/apiClient'
import { useCurrentUser } from '@/lib/hooks'
import type {
  EnrichmentRawInputs,
  EnrichmentStatusResponse,
} from '@/types'
import { cn } from '@/lib/cn'

// =============================================================================
// Enrich.tsx — opt-in profile scan, shown to brand-new users on first login.
// Spec: docs/34_NEW_USER_ENRICHMENT.md
// =============================================================================
// State machine:
//   ask      → user sees the pitch
//   inputs   → optional signals (email pre-filled, LinkedIn, site, phone)
//   scanning → pipeline running, polls status every 2s
//   results  → 3-row preview of what Apollo + the scraper found
//   notfound → free-text fallback (pipeline returned nothing)
// =============================================================================

type Stage = 'ask' | 'inputs' | 'scanning' | 'results' | 'notfound'

interface EnrichProps {
  onComplete: () => void
}

export function Enrich({ onComplete }: EnrichProps) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { data: user } = useCurrentUser()

  const initialStage: Stage = useMemo(() => {
    const s = user?.enrichment?.status
    if (s === 'pending') return 'scanning'
    if (s === 'complete') return 'results'
    if (s === 'failed') return 'notfound'
    return 'ask'
  }, [user?.enrichment?.status])

  const [stage, setStage] = useState<Stage>(initialStage)

  // Keep stage in sync if the user's enrichment record arrives after the
  // component mounts (HAS_AUTH lag).
  useEffect(() => {
    if (stage === 'ask' && initialStage !== 'ask') setStage(initialStage)
  }, [initialStage, stage])

  const [email, setEmail] = useState('')
  const [linkedInUrl, setLinkedInUrl] = useState('')
  const [companySite, setCompanySite] = useState('')
  const [phone, setPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (user?.email && !email) setEmail(user.email)
  }, [user?.email, email])

  const finish = () => {
    queryClient.invalidateQueries({ queryKey: ['user', 'me'] })
    onComplete()
  }

  const handleSkip = async () => {
    setSubmitting(true)
    try {
      await unwrap(apiClient.post('/users/enrich', { skip: true }))
      toast.success("Got it — you can run this later from Settings.")
      finish()
    } catch (e) {
      toast.error("Couldn't skip", (e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleStartScan = async () => {
    setSubmitting(true)
    try {
      const body: EnrichmentRawInputs = {}
      if (email.trim()) body.email = email.trim()
      if (linkedInUrl.trim()) body.linkedInUrl = linkedInUrl.trim()
      if (companySite.trim()) body.companySite = companySite.trim()
      if (phone.trim()) body.phone = phone.trim()
      await apiClient.post('/users/enrich', body)
      setStage('scanning')
    } catch (e) {
      toast.error("Couldn't start the scan", (e as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen w-full bg-jarvis-bg text-jarvis-text flex items-center justify-center p-6">
      <div className="w-full max-w-xl">
        {stage === 'ask' && (
          <AskScreen
            onYes={() => setStage('inputs')}
            onSkip={handleSkip}
            busy={submitting}
          />
        )}
        {stage === 'inputs' && (
          <InputsScreen
            email={email}
            linkedInUrl={linkedInUrl}
            companySite={companySite}
            phone={phone}
            onEmail={setEmail}
            onLinkedIn={setLinkedInUrl}
            onSite={setCompanySite}
            onPhone={setPhone}
            onSubmit={handleStartScan}
            busy={submitting}
          />
        )}
        {stage === 'scanning' && (
          <ScanningScreen
            onComplete={() => setStage('results')}
            onFailed={() => setStage('notfound')}
            onTimeout={finish}
          />
        )}
        {stage === 'results' && (
          <ResultsScreen
            onContinue={finish}
            onEdit={() => setStage('inputs')}
          />
        )}
        {stage === 'notfound' && (
          <NotFoundScreen onComplete={finish} onSkip={finish} />
        )}
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Screen 1 — Ask
// -----------------------------------------------------------------------------

function AskScreen({
  onYes,
  onSkip,
  busy,
}: {
  onYes: () => void
  onSkip: () => void
  busy: boolean
}) {
  return (
    <div className="text-center space-y-8 py-8">
      <div className="flex justify-center">
        <JarvisOrb state="idle" size="md" />
      </div>
      <div className="space-y-4">
        <h1 className="text-3xl font-semibold tracking-tight">Want me to learn about you?</h1>
        <p className="text-jarvis-muted text-base leading-relaxed max-w-md mx-auto">
          I can scan the internet about your business and save what I find to my memory.
          That means every response, briefing, and call is built around you — not a template.
        </p>
      </div>
      <div className="space-y-3 pt-2">
        <button
          type="button"
          onClick={onYes}
          disabled={busy}
          className={cn(
            'inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg',
            'bg-jarvis-blue text-white font-medium text-base',
            'hover:bg-jarvis-blue-bright transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            'min-w-[260px]',
          )}
        >
          Yes, learn about me <ArrowRight size={18} />
        </button>
        <div>
          <button
            type="button"
            onClick={onSkip}
            disabled={busy}
            className="text-sm text-jarvis-muted hover:text-jarvis-text underline-offset-4 hover:underline disabled:opacity-50"
          >
            Skip for now
          </button>
          <p className="text-xs text-jarvis-muted mt-1">I'll be a lot less useful without this.</p>
        </div>
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Screen 2 — Inputs
// -----------------------------------------------------------------------------

function InputsScreen({
  email,
  linkedInUrl,
  companySite,
  phone,
  onEmail,
  onLinkedIn,
  onSite,
  onPhone,
  onSubmit,
  busy,
}: {
  email: string
  linkedInUrl: string
  companySite: string
  phone: string
  onEmail: (v: string) => void
  onLinkedIn: (v: string) => void
  onSite: (v: string) => void
  onPhone: (v: string) => void
  onSubmit: () => void
  busy: boolean
}) {
  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Give me more to work with</h1>
        <p className="text-jarvis-muted text-sm">
          The more you give me, the more accurate the scan. Leave anything blank — I'll still try.
        </p>
      </header>

      <div className="bg-jarvis-surface border border-jarvis-border rounded-xl p-5 space-y-4">
        <Field
          label="Work email"
          value={email}
          onChange={onEmail}
          placeholder="kc@yourcompany.com"
          hint="A company email gets much better results than gmail/yahoo."
        />
        <Field
          label="LinkedIn URL"
          value={linkedInUrl}
          onChange={onLinkedIn}
          placeholder="linkedin.com/in/yourhandle"
          hint="Single highest-signal input — when in doubt, paste this."
        />
        <Field
          label="Company site"
          value={companySite}
          onChange={onSite}
          placeholder="yourcompany.com"
          hint="I'll read your homepage to build your business profile."
        />
        <Field
          label="Phone"
          value={phone}
          onChange={onPhone}
          placeholder="+1 555 555 0100"
          hint="Saved to your profile for ACS setup. Not used for lookups."
        />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-jarvis-muted max-w-sm">
          We only use this to build your Jarvis profile. You can view or delete it in Settings at any time.
        </p>
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy}
          className={cn(
            'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg',
            'bg-jarvis-blue text-white font-medium text-sm',
            'hover:bg-jarvis-blue-bright transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          {busy ? <Loader2 className="animate-spin" size={16} /> : null}
          Start scan <ArrowRight size={16} />
        </button>
      </div>
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  hint?: string
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium text-jarvis-text">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          'w-full px-3 py-2 rounded-md text-sm',
          'bg-jarvis-surface-2 border border-jarvis-border',
          'text-jarvis-text placeholder:text-jarvis-muted/60',
          'focus:outline-none focus:ring-2 focus:ring-jarvis-blue/40 focus:border-jarvis-blue/60',
        )}
        autoComplete="off"
        spellCheck={false}
      />
      {hint && <span className="block text-xs text-jarvis-muted">{hint}</span>}
    </label>
  )
}

// -----------------------------------------------------------------------------
// Screen 3 — Scanning
// -----------------------------------------------------------------------------

interface ScanStepProps {
  label: string
  state: 'pending' | 'active' | 'done'
}

function ScanStep({ label, state }: ScanStepProps) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <span
        className={cn(
          'inline-flex h-5 w-5 items-center justify-center rounded-full border',
          state === 'done' && 'bg-jarvis-green/15 border-jarvis-green text-jarvis-green',
          state === 'active' && 'border-jarvis-blue text-jarvis-blue',
          state === 'pending' && 'border-jarvis-border text-jarvis-muted/50',
        )}
      >
        {state === 'done' ? (
          <CheckCircle2 size={14} />
        ) : state === 'active' ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          <span className="block h-1.5 w-1.5 rounded-full bg-current" />
        )}
      </span>
      <span
        className={cn(
          state === 'pending' ? 'text-jarvis-muted/60' : 'text-jarvis-text',
        )}
      >
        {label}
      </span>
    </div>
  )
}

const SCAN_STEPS = [
  'Looking you up',
  'Finding your business',
  'Reading your website',
  'Building your memory',
]

function ScanningScreen({
  onComplete,
  onFailed,
  onTimeout,
}: {
  onComplete: () => void
  onFailed: () => void
  onTimeout: () => void
}) {
  const [secondsElapsed, setSecondsElapsed] = useState(0)

  // Poll the status endpoint every 2 seconds.
  const { data } = useQuery<EnrichmentStatusResponse>({
    queryKey: ['users', 'enrichment-status'],
    queryFn: () =>
      unwrap<EnrichmentStatusResponse>(apiClient.get('/users/enrichment-status')),
    refetchInterval: 2_000,
    refetchIntervalInBackground: true,
  })

  useEffect(() => {
    const t = window.setInterval(() => setSecondsElapsed((s) => s + 1), 1000)
    return () => window.clearInterval(t)
  }, [])

  // Once status flips, transition.
  useEffect(() => {
    if (!data) return
    if (data.status === 'complete') onComplete()
    else if (data.status === 'failed') onFailed()
  }, [data, onComplete, onFailed])

  // Hard timeout after 25s — push them into the dashboard, pipeline keeps running.
  useEffect(() => {
    if (secondsElapsed >= 25) onTimeout()
  }, [secondsElapsed, onTimeout])

  // Step animation — light up steps as time progresses (purely visual; the
  // pipeline doesn't expose per-step state).
  const activeStep =
    secondsElapsed < 3 ? 0 : secondsElapsed < 8 ? 1 : secondsElapsed < 14 ? 2 : 3

  return (
    <div className="text-center space-y-8 py-8">
      <div className="flex justify-center">
        <JarvisOrb state="thinking" size="md" />
      </div>
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold tracking-tight">Learning about you…</h1>
        <p className="text-sm text-jarvis-muted">This takes about 15 seconds.</p>
      </div>
      <div className="mx-auto max-w-xs text-left space-y-3">
        {SCAN_STEPS.map((label, i) => (
          <ScanStep
            key={label}
            label={label}
            state={i < activeStep ? 'done' : i === activeStep ? 'active' : 'pending'}
          />
        ))}
      </div>
      {secondsElapsed >= 20 && (
        <p className="text-xs text-jarvis-muted">
          Taking longer than usual — we'll keep going in the background.
        </p>
      )}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Screen 4 — Results
// -----------------------------------------------------------------------------

function ResultsScreen({
  onContinue,
  onEdit,
}: {
  onContinue: () => void
  onEdit: () => void
}) {
  const { data, isLoading } = useQuery<EnrichmentStatusResponse>({
    queryKey: ['users', 'enrichment-status'],
    queryFn: () =>
      unwrap<EnrichmentStatusResponse>(apiClient.get('/users/enrichment-status')),
    refetchInterval: false,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-jarvis-muted" />
      </div>
    )
  }

  const preview = data?.preview
  const person = preview?.person || '—'
  const business = preview?.business || '—'
  const topics = preview?.briefingTopics ?? []

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Here's what I found.</h1>
        <p className="text-sm text-jarvis-muted">
          Quick scan complete. You can edit anything that doesn't look right.
        </p>
      </header>

      <div className="bg-jarvis-surface border border-jarvis-border rounded-xl overflow-hidden">
        <Row label="You" value={person} />
        <Divider />
        <Row label="Business" value={business} />
        <Divider />
        <Row label="Briefing pre-loaded with" value={topics.join(' · ') || '—'} />
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onEdit}
          className="text-sm text-jarvis-muted hover:text-jarvis-text underline-offset-4 hover:underline"
        >
          ✏ Edit anything that's wrong
        </button>
        <button
          type="button"
          onClick={onContinue}
          className={cn(
            'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg',
            'bg-jarvis-blue text-white font-medium text-sm',
            'hover:bg-jarvis-blue-bright transition-colors',
          )}
        >
          Looks right. Let's go <ArrowRight size={16} />
        </button>
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-5 py-4 grid grid-cols-[160px_1fr] gap-4 items-start">
      <span className="text-xs font-medium uppercase tracking-wide text-jarvis-muted">
        {label}
      </span>
      <span className="text-sm text-jarvis-text leading-relaxed">{value}</span>
    </div>
  )
}

function Divider() {
  return <div className="h-px bg-jarvis-border" />
}

// -----------------------------------------------------------------------------
// Screen 4B — Not found / free-text fallback
// -----------------------------------------------------------------------------

function NotFoundScreen({
  onComplete,
  onSkip,
}: {
  onComplete: () => void
  onSkip: () => void
}) {
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  const submit = async () => {
    if (!text.trim()) return
    setBusy(true)
    try {
      await apiClient.post('/users/enrichment-fallback', {
        businessSummary: text.trim(),
      })
      onComplete()
    } catch (e) {
      toast.error("Couldn't save", (e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">I didn't find much.</h1>
        <p className="text-sm text-jarvis-muted">
          That's fine — happens with brand-new businesses or personal-email signups.
          Tell me one thing about your business so I'm not starting cold.
        </p>
      </header>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="e.g. I run a landscaping company in Phoenix that serves residential customers."
        rows={3}
        className={cn(
          'w-full px-4 py-3 rounded-lg text-sm leading-relaxed',
          'bg-jarvis-surface border border-jarvis-border',
          'text-jarvis-text placeholder:text-jarvis-muted/60',
          'focus:outline-none focus:ring-2 focus:ring-jarvis-blue/40 focus:border-jarvis-blue/60',
          'resize-none',
        )}
        maxLength={500}
      />

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onSkip}
          className="text-sm text-jarvis-muted hover:text-jarvis-text underline-offset-4 hover:underline"
        >
          Skip
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || text.trim().length < 4}
          className={cn(
            'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg',
            'bg-jarvis-blue text-white font-medium text-sm',
            'hover:bg-jarvis-blue-bright transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          {busy ? <Loader2 className="animate-spin" size={16} /> : null}
          Continue <ArrowRight size={16} />
        </button>
      </div>
    </div>
  )
}
